import { Alert, Select } from "antd";
import { useGetExternalFinalPageSettings } from "api/TenantSlug/Offer/ExternalFinalPageSettings/hooks";
import { useOfferUserList } from "components/Forms/shared/useOfferUserList";
import { FormCol } from "components/UI/Form/FormCol.tsx";
import { FormGrid } from "components/UI/Form/FormGrid.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { ConversionStateInput, PermissionEnum } from "types/enums.ts";
import { FormId } from "types/models.ts";
import { z } from "zod";

const schema = z.object({
    form: z.number().nullable(),
    iframe: z.string(),
    user: z.string().optional(),
});

type OfferFormData = z.infer<typeof schema>;

interface OfferFormProps {
    offer: App.Data.OfferData;
    showUserSelect?: boolean;
    onIframeChange?: (iframe: string) => void;
}

const OfferFormContent: FC<OfferFormProps> = ({ offer, showUserSelect = false, onIframeChange }) => {
    const { t: tF } = usePaldockTranslation("forms.offer-form-detail");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tTags } = usePaldockTranslation("general.tags");
    const generalFormUtils = useGeneralForm<OfferFormData>();
    const { control, fieldNames, setValue } = generalFormUtils;
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const currentUser = userUtils.getAuthenticatedUser();

    const canViewOwn = permissionUtils.hasPermission(PermissionEnum.OfferIframeOwn);
    const canViewOthers = permissionUtils.hasPermission(PermissionEnum.OfferIframeOthers);
    const showUserSelectGuarded = showUserSelect && canViewOthers;

    const { users, defaultUserId } = useOfferUserList({
        offer,
        showUserSelectGuarded,
        canViewOwn,
        currentUser,
    });

    const hasForms = offer.forms && offer.forms.length > 0;
    const [formId, setFormId] = useState<number | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(defaultUserId);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    // Sync selectedUserId with the users list without discarding a valid manual selection.
    // Only (re)select when there is no current selection or it is no longer in the list.
    useEffect(() => {
        if (!users.length) return;

        const hasCurrentSelection = selectedUserId ? users.some((u) => u.id === selectedUserId) : false;
        if (hasCurrentSelection) return;

        const newUserId = defaultUserId && users.some((u) => u.id === defaultUserId) ? defaultUserId : users[0].id;

        setSelectedUserId(newUserId);
        setValue("user", newUserId);
    }, [users, defaultUserId, selectedUserId, setValue]);

    // Fetch fresh external final page settings
    const { data: externalFinalPageData } = useGetExternalFinalPageSettings(offer.id, {});
    const externalFinalPageSettings = externalFinalPageData?.externalFinalPageSettings;

    // Build tag options based on offer settings
    const tagsOptions = useMemo(() => {
        const options = [{ label: tTags("sim"), value: "sim" }];

        if (offer.iframe_auto_submit) {
            options.push({ label: tTags("system_submit"), value: "system_submit" });
        }

        const hasActiveIframeExternalFinalPage = externalFinalPageSettings?.some(
            (setting) => setting.conversion_type === ConversionStateInput.Iframe && setting.active
        );
        if (hasActiveIframeExternalFinalPage) {
            options.push(
                { label: tTags("external_redirect_url"), value: "external_redirect_url" },
                { label: tTags("external_redirect_url_results"), value: "external_redirect_url_results" }
            );
        }

        // Affiliate tracking tags
        options.push(
            { label: tTags("affcid"), value: "affcid" },
            { label: tTags("affs1"), value: "affs1" },
            { label: tTags("affs2"), value: "affs2" },
            { label: tTags("affs3"), value: "affs3" },
            { label: tTags("affs4"), value: "affs4" },
            { label: tTags("affs5"), value: "affs5" },
            { label: tTags("affs6"), value: "affs6" },
            { label: tTags("affs7"), value: "affs7" },
            { label: tTags("affs8"), value: "affs8" },
            { label: tTags("affs9"), value: "affs9" },
            { label: tTags("affs10"), value: "affs10" },
            { label: tTags("destination"), value: "destination" }
        );

        return options;
    }, [offer.iframe_auto_submit, externalFinalPageSettings, tTags]);

    // Helper to set iframe and notify parent
    const setIframe = useCallback(
        (iframe: string) => {
            setValue("iframe", iframe);
            onIframeChange?.(iframe);
        },
        [setValue, onIframeChange]
    );

    // Build iframe code
    const buildIframe = useCallback(
        (fId: number, tags: string[] = []) => {
            const tenantSlug = userUtils.getActiveTenant()?.slug_name;
            const userId = selectedUserId ?? (canViewOwn ? currentUser?.id : undefined);

            if (!tenantSlug || !userId) {
                return "";
            }

            const baseUrl = `${import.meta.env.VITE_CDN_URL}/js/piframe.js?id=${fId}&ts=${tenantSlug}&a=${userId}&o=${offer.id}`;

            const url = new URL(baseUrl);
            tags.forEach((tag) => {
                url.searchParams.append(tag, "REPLACE_ME");
            });

            return `<div><script type="module" src="${url.toString()}"></script></div>`;
        },
        [offer.id, userUtils, selectedUserId, canViewOwn, currentUser?.id]
    );

    // Set initial form and iframe
    useEffect(() => {
        if (!hasForms) {
            setIframe(tF("messages.select_form_structure"));
            return;
        }

        const selectedFormId = formId || offer.forms?.[0]?.id;
        if (selectedFormId) {
            if (!formId) {
                setFormId(selectedFormId);
            }
            setValue("form", selectedFormId);
            setIframe(buildIframe(selectedFormId, selectedTags));
        }
    }, [offer.forms, formId, hasForms, buildIframe, setIframe, setValue, tF, selectedTags]);

    const handleChangeForm = (value: number) => {
        setFormId(value);
    };

    const handleUserChange = (val: string) => {
        setSelectedUserId(val);
        setValue("user", val);
    };

    const changeTags = (tags: string[]) => {
        setSelectedTags(tags);
    };

    const colsCount = 1 + (showUserSelectGuarded ? 1 : 0);
    const cols = Math.min(colsCount, 4) as 1 | 2 | 3 | 4;

    return (
        <FormCol>
            {hasForms ? (
                <>
                    <FormGrid cols={cols}>
                        <FormItem control={control} name={fieldNames.form.name} label={tF("fields.form_design.label")}>
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                                options={[...(offer.forms ?? [])]
                                    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
                                    .map((form) => ({
                                        label: form.name,
                                        value: form.id,
                                    }))}
                                placeholder={tF("fields.form_design.placeholder")}
                                onChange={handleChangeForm}
                            />
                        </FormItem>
                        {showUserSelectGuarded && (
                            <FormItem
                                control={control}
                                name={fieldNames.user?.name || "user"}
                                label={tFields("user.label")}
                            >
                                <Select
                                    value={selectedUserId}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    placeholder={tFields("user.placeholder")}
                                    options={users.map((u) => ({
                                        label: u.nickname ?? u.email ?? tFields("user.no_name"),
                                        value: u.id,
                                    }))}
                                    onChange={handleUserChange}
                                />
                            </FormItem>
                        )}
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem control={control} name={fieldNames.iframe.name} label={tF("fields.iframe.label")}>
                            <TextArea name="iframe" appearance="readonly" readOnly />
                        </FormItem>
                        <TagSelect options={tagsOptions} value={selectedTags} onChange={changeTags} />
                    </FormGrid>
                </>
            ) : (
                <Alert type="info" showIcon message={tF("messages.no_active_form")} className="mb-6" />
            )}
        </FormCol>
    );
};

export const OfferFormDetailForm: FC<OfferFormProps> = ({ offer, showUserSelect, onIframeChange }) => {
    const defaultValues: OfferFormData = {
        form: null,
        iframe: "",
    };

    return (
        <GeneralFormProvider<OfferFormData>
            schema={schema}
            defaultValues={defaultValues}
            id={FormId.OfferFormDetailForm}
        >
            <OfferFormContent offer={offer} showUserSelect={showUserSelect} onIframeChange={onIframeChange} />
        </GeneralFormProvider>
    );
};
